<script lang="ts">
  import Separator from "$shared/components/Separator.svelte";
  import { IntervalModel } from "$models";
  import CardInline from "$shared/components/CardInline.svelte";
  import CustomButton from "$shared/components/CustomButton.svelte";
  import { Hor, Spacer } from "$components";
  import api, {
    fromApi,
    intoModel,
  } from "$shared/lib/api-parkito2.svelte.ts";
  import ListNavigationItem from "$shared/components/ListNavigationItem.svelte";
  import ValCheckbox from "$shared/components/ValCheckbox.svelte";
  import { Form, Validate } from "$shared/lib/validation_next";
  import { type DebtorModel, PdfModel } from "$shared/models/mod.ts";
  import { LicensePlateManagerCard, ModalDialog } from "$components";
  import LoadingIndicator from "$shared/components/LoadingIndicator.svelte";
  import { Header, Toast } from "$components";
  import { AgreementOfferReply } from "../../../apis/parkito";
  import { withApi } from "$shared/lib/api-parkito";
  import { pop } from "svelte-spa-router";
  import { formatNumber } from "$shared/utils/misc";
  import { formatDate } from "$shared/utils/dateTime";
  import { myPages } from "../stores/myPagesData.svelte.ts";

  // Exported variable with name `params` is set
  // by svelte-spa-router. Check App.svelte
  type Props = {
    params: { id: string };
  };
  const { params }: Props = $props();

  const fieldAgreementIsReadAndAccepted = Form.checkbox(Validate.boolean());
  let declineAgreementDialog = $state(false);
  const agreement = $derived(
    $myPages.call.data?.findAgreementById(params.id)?.agreement,
  );

  const debtor = $derived(
    $myPages.call.data?.findDebtorByAgreementId(params.id)?.debtor,
  );

  const site = $derived(
    $myPages.call.data?.findSiteByAgreementId(params.id),
  );

  const replyToAgreementOffer = async (
    agreementId: string,
    decision: AgreementOfferReply,
    debtor: DebtorModel,
  ) => {
    if (debtor.data.deliveryMethod === "none") {
      Toast.error("Leveransmetod måste anges");
      return;
    }
    const res = await withApi((api) => {
      return api.replyToAgreementOffer(agreementId, decision);
    });

    if (res.isOk()) {
      Toast.info("Erbjudandet har besvarats");
      $myPages.reload();
    } else {
      Toast.error("Kunde inte besvara avtalet");
    }
  };

  const toggleDeclineAgreementDialog = () => {
    declineAgreementDialog = !declineAgreementDialog;
  };

  const agreementPdf = fromApi(() =>
    api
      .getAgreementPdf({ path: { id: params.id } })
      .then(intoModel(PdfModel))
  );

  const fetchPdf = async () => {
    const isLoadedFromApp =
      new URLSearchParams(window.location.search).get("app") === "true";
    const pdf = await $agreementPdf.load();
    if (pdf.data) {
      pdf.data.openPdf(isLoadedFromApp);
    } else {
      Toast.error("Kunde inte hämta PDF");
    }
  };
</script>

<div>
  <Hor margin="l">
    <button
      onclick={() => {
        pop();
      }}
      class="-ml-[8px] min-[480px]:hidden"
    >
      <img
        src="./icons/arrow-left-small.svg"
        class="w-[24px] inline p-[8px] pb-[11px]"
        alt="arrow-left"
      />
    </button>
    <Header size="3">
      {#if $myPages.isOk() && agreement}
        {agreement.translateContractType()} {agreement.number}
        <span class={agreement.getStateColor()}>
          ●
        </span>
      {/if}
    </Header>

    <Spacer />
    <CustomButton
      loading={$agreementPdf.isLoading()}
      outset={false}
      type="ghost"
      onClick={fetchPdf}
    >
      <img
        src="/icons/download.svg"
        alt=""
        class="inline mr-[6px] w-4"
      />
      Ladda ner PDF
    </CustomButton>
  </Hor>
  <Separator customClass="max-mobile:hidden" />
  {#if $myPages.isLoading()}
    <LoadingIndicator visible={true} customClass="py-[96px]" />
  {:else if $myPages.isOk() && agreement}
    <div>
      <div
        class="w-full min-[840px]:columns-2 columns-1 p-[24px] max-mobile:px-[8px]"
      >
        <!-- Agreement information -->
        <CardInline title={agreement.translateContractType()}>
          {#if debtor !== undefined}
            <div>
              <ListNavigationItem
                title={"Avtalsnummer"}
                textRight={agreement.number.toString() ?? "-"}
                isFirst={true}
              />
              <ListNavigationItem title={"Status"}>
                <span class={agreement.getStateColor()}>
                  ●
                </span>
                {agreement.translateAgreementState()}
              </ListNavigationItem>
              {#if agreement.isOffered()}
                <ListNavigationItem
                  title={"Erbjudandetid"}
                  textRight={`${agreement.offerDaysLeft()} dagar kvar`}
                />
              {/if}
              <ListNavigationItem
                title="Uppsägningstid"
                textRight={`${
                  new IntervalModel(agreement.data.noticePeriod)
                    .format()
                }`}
              />
              <ListNavigationItem title="Giltighetstid">
                {
                  formatDate(
                    agreement.data.startDate,
                    "YYYY-MM-DD",
                  )
                } → {
                  agreement.data.endDate
                    ? formatDate(
                      agreement.data.endDate,
                      "YYYY-MM-DD",
                    )
                    : "tills vidare"
                }
              </ListNavigationItem>

              {#if agreement.data.period}
                <ListNavigationItem title="Avtalsperiod">
                  {new IntervalModel(agreement.data.period).format()}
                </ListNavigationItem>
              {/if}
            </div>
          {:else}
            <p class="px-[16px] text-[14px]">
              Parkördata kunde ej laddas in.
              <a
                class="underline"
                href={`#/debtors/${agreement.data.debtorId}`}
              >
                Visa parkör.
              </a>
            </p>
          {/if}
        </CardInline>
        <!-- Agreement user -->
        <CardInline title="Avtalstagare">
          {#if debtor}
            <div>
              <ListNavigationItem
                title={debtor.isPerson()
                  ? "Personnummer"
                  : "Organisationsnummer"}
                textRight={debtor.getSid()}
                isFirst={true}
              />
              <ListNavigationItem
                title={"Namn"}
                textRight={debtor.data.name ?? "-"}
              />
              <ListNavigationItem
                title={"E-post kontakt"}
                textRight={debtor.data.contactEmail ?? "-"}
              />
              <ListNavigationItem
                title={"Telefon"}
                textRight={debtor.data.phone ?? "-"}
                isLast={true}
              />
            </div>
          {:else}
            <p class="px-[16px] text-[14px]">
              Parkördata kunde ej laddas in. <a
                class="underline"
                href={`#/debtors/${agreement.data.debtorId}`}
              >Visa parkör.</a>
            </p>
          {/if}
        </CardInline>

        <!-- Object -->
        <CardInline title="Objekt">
          {#if site}
            <div>
              <ListNavigationItem
                title="Anläggning"
                textRight={site.name ?? "-"}
                isFirst={true}
              />
              <ListNavigationItem
                title="Gatuadress"
                textRight={site.rentalInfo?.address ?? "-"}
              />
              <ListNavigationItem
                title="Plats(er)"
                textRight={agreement.spots.map((s) => s.name).join(
                  ", ",
                )}
                isLast={true}
              />
            </div>
          {:else}
            <p class="px-[16px] text-[14px]">
              Anläggningsdata kunde ej laddas in.
            </p>
          {/if}
        </CardInline>

        {#if agreement.permit}
          <LicensePlateManagerCard
            permit={agreement.permit}
            reloadData={() => $myPages.reload()}
            accessLevel="Owner"
            dpoAdmin
          />
        {/if}

        <!-- Payment -->
        <CardInline title="Betalning">
          {#if debtor}
            <div>
              {#if agreement.isPermit()}
                <ListNavigationItem
                  title="Betalmetod"
                  link={`#/profile/${debtor.data.id}`}
                  isFirst={true}
                  isLast={true}
                >
                  Kort
                </ListNavigationItem>
              {:else}
                <ListNavigationItem
                  title="Betalmetod"
                  link={`#/profile/${debtor.data.id}/payment-method`}
                  isFirst={true}
                >
                  {debtor.translatePaymentMethodType()}
                </ListNavigationItem>

                <ListNavigationItem
                  title="Leveransmetod"
                  link={`#/profile/${debtor.data.id}/delivery-method`}
                  customClass={debtor.data.deliveryMethod === "none"
                    ? "bg-red-1 hover:bg-red-2"
                    : ""}
                >
                  {#if debtor.data.deliveryMethod === "none"}
                    <span class="text-red-5 font-bold"
                    >Leveransmetod saknas</span>
                  {:else}
                    {debtor.translateDeliveryMethodType()}
                  {/if}
                </ListNavigationItem>
                <ListNavigationItem
                  title="Fakturering start"
                  textRight={agreement.data.invoiceFrom}
                  isLast={true}
                />
              {/if}
            </div>
          {/if}
        </CardInline>

        <!-- Product specification -->
        <CardInline title="Produktspecifikation">
          <div>
            {#each agreement.derivedArticles as article, index}
              <ListNavigationItem title={article.name} isFirst={index === 0}>
                <div class="text-right">
                  <span>{
                      formatNumber(
                        article.price.sub + article.price.vat,
                      )
                    } SEK<span><br />
                      <div class="text-[12px]">
                        Exkl. moms {formatNumber(article.price.sub)}; moms {
                          formatNumber(
                            article.price.vat,
                          )
                        }
                      </div>
                    </span></span>
                </div>
              </ListNavigationItem>
            {/each}
            <ListNavigationItem title="Periodkostnad" isLast={true}>
              <div class="text-right">
                <span class="font-bold">{
                    formatNumber(agreement.getArticleSum())
                  } SEK</span>
                <br />
                <span class="text-[12px]">Exkl. moms {
                    formatNumber(
                      agreement.getArticleSum(false),
                    )
                  }; moms {
                    formatNumber(
                      agreement.getArticleSum(true) -
                        agreement.getArticleSum(false),
                    )
                  }</span>
              </div>
            </ListNavigationItem>
          </div>
          <p class="text-[14px] px-[16px] pt-[16px]">
            Alla priser är i svenska kronor, SEK.
          </p>
        </CardInline>

        <div class="p-[8px] rounded-[8px] break-inside-avoid-column">
          <h3 class="text-[14px] font-bold text-[#181818]">Villkor</h3>

          <p class="mt-[8px]">
            <a
              href={`#/agreements/${agreement.id}/terms`}
              class="underline"
            >Villkor för avtal {agreement.number}</a>
          </p>

          {#if agreement.isOffered() && !agreement.isPermit()}
            <div class="pt-[32px]">
              <h3 class="text-[14px] font-bold text-[#181818] inline">
                Godkänn avtal
              </h3>
              <span class="text-red-5 font-bold">* (obligatorisk)</span>
              <ValCheckbox
                name="protected-identity"
                field={fieldAgreementIsReadAndAccepted}
                label="Jag intygar att jag har läst avtalet och godkänner villkoren."
                customClass="py-[8px]"
              />
            </div>
          {/if}
        </div>
      </div>

      <!-- Buttons -->
      <div>
        <div class="flex pt-[16px] pb-[48px] px-[24px] justify-end">
          {#if agreement.isOffered() && debtor}
            <CustomButton
              type="ghost"
              onClick={toggleDeclineAgreementDialog}
              customClass="w-[200px] mr-[16px]"
            >
              Neka
            </CustomButton>

            <CustomButton
              type="primary"
              onClick={() => {
                replyToAgreementOffer(
                  agreement.id ?? "",
                  AgreementOfferReply.ACCEPT,
                  debtor,
                );
              }}
              disabled={$fieldAgreementIsReadAndAccepted.res.isOk() &&
                $fieldAgreementIsReadAndAccepted.res.value === false}
              customClass="w-[200px]"
            >Acceptera</CustomButton>
          {/if}
        </div>
      </div>
    </div>
  {/if}
  <!-- Decline agreement modal dialog -->
  {#if declineAgreementDialog}
    <ModalDialog onBackdropPressed={toggleDeclineAgreementDialog}>
      <div class="px-[24px] py-[24px] max-mobile:px-[16px]">
        <h2 class="text-[#181818] text-[18px]">Bekräfta neka avtal</h2>
      </div>
      <Separator />
      {#if debtor && agreement}
        <div class="px-[24px] py-[16px] max-mobile:px-[16px]">
          <p class="py-[16px]">Är du säker på att du vill neka avtalet?</p>
          <div class="flex pt-[24px] justify-end">
            <CustomButton
              type="ghost"
              onClick={toggleDeclineAgreementDialog}
              customClass="mr-[8px]"
            >Avbryt</CustomButton>
            <CustomButton
              type="warning"
              onClick={() => {
                replyToAgreementOffer(
                  agreement.id ?? "",
                  AgreementOfferReply.REJECT,
                  debtor,
                );
              }}
              customClass="w-[200px]"
            >Neka avtal</CustomButton>
          </div>
        </div>
      {/if}
    </ModalDialog>
  {/if}
</div>
